// Sends 1:1 Microsoft Teams chat messages as the signed-in person, through
// Microsoft Graph with a delegated token. Replaces the "MS Teams Blast" Make
// scenario: each recipient gets their own chat with the sender (the existing
// one is reused when it already exists).

// DEPENDENCIES
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";

const GRAPH = "https://graph.microsoft.com/v1.0";
const TEAMS_SCOPES = ["Chat.Create", "ChatMessage.Send"];

// Placeholder replaced with each recipient's first name
export const FIRST_NAME_TOKEN = "{first_name}";

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const MAX_RETRIES = 3;

// Temporary failures worth retrying: throttling (429), gateway / unavailable
// (502-504) and dropped connections (no response). A message POST that timed
// out may still have been delivered, so a retry can rarely send it twice.
const RETRY_STATUSES = [429, 502, 503, 504];
const isRetryable = (error) =>
	!error?.response || RETRY_STATUSES.includes(error.response.status);

/**
 * A Graph token that can create chats and send chat messages, showing
 * Microsoft's sign-in/consent popup if needed
 */
export async function getTeamsToken(instance, account) {
	try {
		const res = await instance.acquireTokenSilent({
			scopes: TEAMS_SCOPES,
			account,
		});
		return res.accessToken;
	} catch (error) {
		if (!(error instanceof InteractionRequiredAuthError)) throw error;
		const res = await instance.acquireTokenPopup({
			scopes: TEAMS_SCOPES,
			account,
		});
		return res.accessToken;
	}
}

/**
 * The Graph user key for an employee: the Azure AD object id from teams_id
 * (stored bare or as a graph.microsoft.com/v1.0/users/<id> URL), else email
 */
export const userKeyOf = (employee) => {
	const id = String(employee?.teams_id || "").match(GUID_RE)?.[0];
	return id || String(employee?.email || "").trim() || null;
};

const memberOf = (userKey) => ({
	"@odata.type": "#microsoft.graph.aadUserConversationMember",
	roles: ["owner"],
	"user@odata.bind": `${GRAPH}/users('${userKey}')`,
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// POST to Graph, retrying temporary failures (waiting Retry-After when given,
// else 1s, 2s, 4s)
const graphPost = async (path, body, token) => {
	for (let attempt = 0; ; attempt++) {
		try {
			const res = await axios.post(`${GRAPH}${path}`, body, {
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			});
			return res.data;
		} catch (error) {
			if (isRetryable(error) && attempt < MAX_RETRIES) {
				const wait =
					Number(error.response?.headers?.["retry-after"]) ||
					2 ** attempt;
				await sleep(wait * 1000);
				continue;
			}
			throw error;
		}
	}
};

export const graphErrorText = (error) =>
	error?.response?.data?.error?.message || error?.message || "Unknown error";

/**
 * Message HTML for one recipient, with the first-name token filled in
 */
export const personalize = (html, employee) => {
	const name = String(employee?.first_name || "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
	return html.split(FIRST_NAME_TOKEN).join(name);
};

/**
 * Opens (or reuses) the 1:1 chat between the sender and a recipient and posts
 * the message to it
 * @param {string} senderKey - the signed-in person's Azure AD object id
 */
export async function sendTeamsMessage(token, senderKey, recipientKey, html) {
	const chat = await graphPost(
		"/chats",
		{
			chatType: "oneOnOne",
			members: [memberOf(senderKey), memberOf(recipientKey)],
		},
		token,
	);
	await graphPost(
		`/chats/${chat.id}/messages`,
		{ body: { contentType: "html", content: html } },
		token,
	);
}
